const COLORS = {
  Equity: "#3ecfad",
  Debt: "#38bdf8",
  Gold: "#fbbf24",
  Hybrid: "#a78bfa",
  International: "#fb923c",
  Cash: "#94a3b8",
  Others: "#64748b",
};

const SECTOR_COLORS = {
  "Large Cap": "#2da88c",
  "Mid Cap": "#22d3ee",
  "Small Cap": "#fb923c",
  Flexi: "#a78bfa",
  "Sector / Thematic": "#f472b6",
  Debt: "#38bdf8",
  Gold: "#fbbf24",
  Hybrid: "#c084fc",
  International: "#60a5fa",
  Others: "#64748b",
};

const EXPECTED = {
  Equity: 12.5,
  Debt: 6.8,
  Gold: 8.0,
  Hybrid: 9.5,
  International: 11.0,
  Cash: 4.0,
  Others: 8.0,
};

function classifyAsset(name) {
  const n = name.toLowerCase();
  if (/gold|silver|sgb/.test(n)) return "Gold";
  if (/liquid|overnight|money\s*market|gilt|duration|debt|bond|income|arbitrage|corporate\s*bond|banking\s*&?\s*psu|floater|ultra\s*short|low\s*duration|short\s*duration/.test(n))
    return "Debt";
  if (/international|nasdaq|s&p|us\s|global|world|overseas|fof.*intl/.test(n))
    return "International";
  if (/hybrid|balanced|aggressive\s*hybrid|conservative\s*hybrid|multi\s*asset|equity\s*savings/.test(n))
    return "Hybrid";
  if (/cash|savings/.test(n)) return "Cash";
  if (/equity|flexi|mid\s*cap|small\s*cap|large\s*cap|index|nifty|sensex|elss|tax\s*saver|value|growth|multicap|multi\s*cap|focussed|focused|contra|dividend|pharma|bank|infra|consumption|digital|technology|etf/.test(n))
    return "Equity";
  return "Others";
}

function classifyStyle(name) {
  const n = name.toLowerCase();
  if (/gold|silver|sgb/.test(n)) return "Gold";
  if (/liquid|overnight|gilt|duration|debt|bond|income|arbitrage/.test(n)) return "Debt";
  if (/international|nasdaq|global|overseas/.test(n)) return "International";
  if (/hybrid|balanced|multi\s*asset|equity\s*savings/.test(n)) return "Hybrid";
  if (/small\s*cap/.test(n)) return "Small Cap";
  if (/mid\s*cap/.test(n)) return "Mid Cap";
  if (/large\s*cap|bluechip|nifty\s*50|sensex/.test(n)) return "Large Cap";
  if (/flexi|multi\s*cap|multicap/.test(n)) return "Flexi";
  if (/pharma|bank|infra|consumption|digital|technology|thematic|sector|psu|defence|manufacturing/.test(n))
    return "Sector / Thematic";
  if (/equity|index|elss|etf|value|growth|contra|focussed|focused/.test(n)) return "Large Cap";
  return "Others";
}

function pctMap(entries, total, colorMap) {
  if (total <= 0) return [];
  const list = Object.entries(entries)
    .map(([label, value]) => ({
      label,
      value,
      pct: Math.round((value / total) * 1000) / 10,
      color: colorMap[label] || COLORS.Others,
    }))
    .filter((x) => x.pct > 0)
    .sort((a, b) => b.pct - a.pct);

  // Fix rounding so sum ~ 100
  if (list.length) {
    const sum = list.reduce((s, x) => s + x.pct, 0);
    list[0].pct = Math.round((list[0].pct + (100 - sum)) * 10) / 10;
  }
  return list;
}

function healthLabel(score) {
  if (score >= 80) return "Strong";
  if (score >= 65) return "Good";
  if (score >= 50) return "Fair";
  return "Needs Work";
}

function riskLabel(score) {
  if (score >= 75) return "Aggressive — better suited for 8+ year horizons.";
  if (score >= 55) return "Moderately aggressive — suited for 5–7+ year horizons.";
  if (score >= 35) return "Balanced — suited for medium-term goals.";
  return "Conservative — capital preservation oriented.";
}

/**
 * Analyze normalized holdings into scores, allocations, and recommendations.
 */
export function analyzePortfolio(holdings, meta = {}) {
  if (!holdings?.length) {
    throw new Error("Add at least one holding with a positive current value.");
  }

  const enriched = holdings.map((h) => {
    const amount = Number(h.amount) || 0;
    const invested = h.invested === "" || h.invested == null ? null : Number(h.invested);
    return {
      ...h,
      amount,
      invested: Number.isFinite(invested) && invested > 0 ? invested : null,
      asset: classifyAsset(h.fundName),
      style: classifyStyle(h.fundName),
    };
  });

  const totalValue = enriched.reduce((s, h) => s + h.amount, 0);
  const totalInvested = enriched.reduce(
    (s, h) => s + (h.invested != null ? h.invested : 0),
    0
  );
  const hasCost = enriched.some((h) => h.invested != null);

  const assetsAcc = {};
  const stylesAcc = {};
  enriched.forEach((h) => {
    assetsAcc[h.asset] = (assetsAcc[h.asset] || 0) + h.amount;
    stylesAcc[h.style] = (stylesAcc[h.style] || 0) + h.amount;
  });

  const assets = pctMap(assetsAcc, totalValue, COLORS);
  const sectors = pctMap(stylesAcc, totalValue, SECTOR_COLORS);

  const weights = enriched.map((h) => h.amount / totalValue);
  const hhi = weights.reduce((s, w) => s + w * w, 0);
  const topWeight = Math.max(...weights);
  const equityPct =
    ((assetsAcc.Equity || 0) +
      (assetsAcc.International || 0) +
      (assetsAcc.Hybrid || 0) * 0.65) /
    totalValue;
  const midSmallPct =
    ((stylesAcc["Mid Cap"] || 0) + (stylesAcc["Small Cap"] || 0)) / totalValue;
  const debtPct = (assetsAcc.Debt || 0) / totalValue;
  const goldPct = (assetsAcc.Gold || 0) / totalValue;
  const elssCount = enriched.filter((h) => /elss|tax\s*saver/i.test(h.fundName)).length;
  const flexiOverlap = enriched.filter((h) => /flexi|multi\s*cap|multicap/i.test(h.fundName)).length;

  // Diversification: more funds + lower HHI = better
  const n = enriched.length;
  let diversification = Math.round(
    Math.min(95, Math.max(25, (1 - hhi) * 70 + Math.min(n, 12) * 2.5))
  );
  if (topWeight > 0.45) diversification = Math.min(diversification, 48);
  if (topWeight > 0.6) diversification = Math.min(diversification, 35);

  // Risk: equity weight + mid/small + concentration
  let riskScore = Math.round(
    Math.min(
      95,
      Math.max(
        15,
        equityPct * 70 + midSmallPct * 25 + topWeight * 20 + (1 - debtPct) * 5
      )
    )
  );

  // Expected return (forward-looking, allocation weighted)
  const expectedReturn =
    Math.round(
      enriched.reduce((s, h) => {
        const w = h.amount / totalValue;
        return s + w * (EXPECTED[h.asset] || EXPECTED.Others);
      }, 0) * 10
    ) / 10;

  // Absolute return if cost basis present
  let absoluteReturn = null;
  if (hasCost && totalInvested > 0) {
    absoluteReturn =
      Math.round(((totalValue - totalInvested) / totalInvested) * 1000) / 10;
  }

  // Est. CAGR / XIRR proxy — use absolute return annualized if cost known (assume 3y if unknown tenure)
  // Without cashflows, true XIRR isn't available — we provide allocation-based estimate + absolute when possible
  const estCagr = expectedReturn;
  const xirr =
    absoluteReturn != null
      ? Math.round((absoluteReturn / 3) * 10) / 10 // rough annualization assuming ~3y average hold
      : expectedReturn;

  // Health score
  let healthScore = 55;
  healthScore += Math.min(20, n * 2);
  healthScore += diversification * 0.15;
  if (equityPct >= 0.4 && equityPct <= 0.8) healthScore += 8;
  if (debtPct >= 0.1 && debtPct <= 0.4) healthScore += 6;
  if (goldPct >= 0.03 && goldPct <= 0.15) healthScore += 4;
  if (topWeight > 0.4) healthScore -= 12;
  if (topWeight > 0.55) healthScore -= 10;
  if (midSmallPct > 0.35) healthScore -= 8;
  if (equityPct > 0.9) healthScore -= 10;
  if (equityPct < 0.2 && n > 1) healthScore -= 5;
  healthScore = Math.round(Math.min(95, Math.max(28, healthScore)));

  const benchmarks = [
    {
      name: "Nifty 50 (long-term avg)",
      you: expectedReturn,
      bench: 12.0,
    },
    {
      name: "Balanced 70/30 portfolio",
      you: expectedReturn,
      bench: 10.5,
    },
    {
      name: "Category blend (est.)",
      you: expectedReturn,
      bench: Math.round((expectedReturn * 0.92 + 11) * 5) / 10,
    },
  ];

  const recommendations = [];

  if (riskScore >= 70 || midSmallPct > 0.3) {
    recommendations.push({
      key: "risk",
      title: "Reduce Risk",
      desc: `Equity-sensitive exposure is elevated (~${Math.round(equityPct * 100)}% effective equity${
        midSmallPct > 0.05 ? `, with ~${Math.round(midSmallPct * 100)}% mid/small-cap` : ""
      }). Consider shifting 8–12% into large-cap or short-duration debt.`,
    });
  } else {
    recommendations.push({
      key: "risk",
      title: "Maintain Risk Discipline",
      desc: `Current risk score is ${riskScore}/100. Keep SIPs aligned to goals and avoid chasing high-beta themes without a plan.`,
    });
  }

  if (topWeight > 0.35 || Math.abs(equityPct - 0.7) > 0.15) {
    recommendations.push({
      key: "rebalance",
      title: "Rebalance Portfolio",
      desc: `Largest holding is ~${Math.round(topWeight * 100)}% of the portfolio. Rebalance toward a target mix (e.g. 60–70% equity / 20–30% debt / 5–10% gold).`,
    });
  } else {
    recommendations.push({
      key: "rebalance",
      title: "Rebalance Portfolio",
      desc: "Allocation looks reasonably balanced. Review quarterly and rebalance if any sleeve drifts by more than 5%.",
    });
  }

  if (flexiOverlap >= 2) {
    recommendations.push({
      key: "funds",
      title: "Better Mutual Funds",
      desc: `You hold ${flexiOverlap} flexi/multi-cap style funds — possible overlap. Consolidate into 1–2 high-conviction funds to reduce duplication.`,
    });
  } else if (n > 10) {
    recommendations.push({
      key: "funds",
      title: "Better Mutual Funds",
      desc: `You have ${n} holdings. Too many schemes can dilute returns. Consider consolidating similar categories into fewer quality funds.`,
    });
  } else {
    recommendations.push({
      key: "funds",
      title: "Better Mutual Funds",
      desc: "Fund count looks manageable. Prefer consistent top-quartile performers and review expense ratios on regular plans.",
    });
  }

  if (elssCount === 0) {
    recommendations.push({
      key: "tax",
      title: "Tax Saving Suggestions",
      desc: "No ELSS detected. If you have Section 80C headroom, a monthly ELSS SIP can combine tax saving with equity growth (3-year lock-in).",
    });
  } else {
    recommendations.push({
      key: "tax",
      title: "Tax Saving Suggestions",
      desc: `ELSS already present (${elssCount}). Harvest tax-loss opportunities in non-ELSS equity if needed, and avoid unnecessary churn within 1 year.`,
    });
  }

  if (goldPct < 0.03 || debtPct < 0.1 || !assetsAcc.International) {
    const gaps = [];
    if (debtPct < 0.1) gaps.push("short-duration debt (~10–20%)");
    if (goldPct < 0.03) gaps.push("gold ETF/fund (~5%)");
    if (!assetsAcc.International) gaps.push("international equity (~5–10%)");
    recommendations.push({
      key: "alloc",
      title: "Asset Allocation Suggestions",
      desc: `Consider adding ${gaps.join(", ")} to improve diversification across market cycles.`,
    });
  } else {
    recommendations.push({
      key: "alloc",
      title: "Asset Allocation Suggestions",
      desc: "Core sleeves (equity / debt / gold) look represented. Keep international exposure capped and rebalance annually.",
    });
  }

  return {
    holdings: enriched.map((h) => ({
      fundName: h.fundName,
      amount: h.amount,
      units: h.units,
      invested: h.invested,
      asset: h.asset,
      style: h.style,
      weight: Math.round((h.amount / totalValue) * 1000) / 10,
    })),
    totalValue: Math.round(totalValue),
    totalInvested: hasCost ? Math.round(totalInvested) : null,
    absoluteReturn,
    healthScore,
    healthLabel: healthLabel(healthScore),
    riskScore,
    riskBlurb: riskLabel(riskScore),
    diversification,
    diversificationBlurb:
      diversification >= 70
        ? "Healthy spread across holdings and categories."
        : diversification >= 50
          ? "Moderate spread; watch concentration in top holdings."
          : "Concentrated portfolio — diversification should be a priority.",
    xirr,
    cagr: estCagr,
    expectedReturn,
    returnNote:
      absoluteReturn != null
        ? "XIRR shown is a rough annualized estimate from cost vs current value (assumes ~3 years). For exact XIRR, share transaction history with an advisor."
        : "XIRR/CAGR from cashflows need transaction history. Figures below are allocation-weighted expected long-term returns.",
    assets,
    sectors,
    benchmarks,
    recommendations,
    meta: {
      ...meta,
      holdingsCount: enriched.length,
      parsed: true,
    },
  };
}
